import axios from 'axios'
import appConfig from 'configs/app.config'
import { TOKEN_TYPE, REQUEST_HEADER_AUTH_KEY } from 'constants/api.constant'
import { PERSIST_STORE_NAME } from 'constants/app.constant'
import deepParseJson from 'utils/deepParseJson'
import store from '../store'
import { onSignOutSuccess } from '../store/auth/sessionSlice'

// QPM SDK for API call tracking — QPM_ENABLED 가 false 면 아무것도 하지 않는다
import { getQPM } from '@qpm/sdk/frontend'
import { QPM_ENABLED } from 'lib/qpm-sdk/enabled'
import { observeAxiosResponse, observeAxiosError } from './maintenance'

const unauthorizedCode = [401]

const BaseService = axios.create({
    timeout: 60000,
    baseURL: appConfig.apiPrefix,
})

BaseService.interceptors.request.use(
    (config) => {
        // QPM: API 호출 추적 시작 (꺼져 있으면 스팬을 만들지 않으므로 아래 응답 처리도 모두 건너뛴다)
        const qpm = QPM_ENABLED ? getQPM() : null
        if (qpm) {
            try {
                const span = qpm.startTrace(`${config.method?.toUpperCase() || 'GET'} ${config.url}`, {
                    type: 'api',
                    critical: true
                })
                config._qpmSpan = span

                // Context propagation 헤더 추가
                if (span.context) {
                    config.headers = config.headers || {}
                    config.headers['x-qpm-trace'] = span.context.toQPMHeader()
                    config.headers['traceparent'] = span.context.toTraceparent()
                }
            } catch (err) {
                // QPM 오류가 실제 요청을 막지 않도록 함
                console.warn('[QPM] Span creation failed:', err.message)
            }
        }

        // 기존 토큰 처리
        const rawPersistData = localStorage.getItem(PERSIST_STORE_NAME)
        const persistData = deepParseJson(rawPersistData)

        let accessToken = persistData?.auth?.session?.token

        if (!accessToken) {
            const { auth } = store.getState()
            accessToken = auth?.session?.token
        }

        if (accessToken) {
            config.headers[
                REQUEST_HEADER_AUTH_KEY
            ] = `${TOKEN_TYPE}${accessToken}`
        }

        return config
    },
    (error) => {
        return Promise.reject(error)
    }
)

BaseService.interceptors.response.use(
    (response) => {
        // QPM: API 호출 성공 시 스팬 종료
        if (response.config._qpmSpan) {
            try {
                response.config._qpmSpan.setTag('http.status', response.status)
                response.config._qpmSpan.end()
            } catch (err) {
                console.warn('[QPM] Span end failed:', err.message)
            }
        }
        // 큐베이스 점검(쓰기 동결) 헤더 관찰
        observeAxiosResponse(response)
        return response
    },
    (error) => {
        // QPM: API 호출 실패 시 에러 기록 후 스팬 종료
        if (error.config?._qpmSpan) {
            try {
                error.config._qpmSpan.setError({
                    message: error.message,
                    code: error.response?.status || 'NETWORK_ERROR'
                })
                error.config._qpmSpan.end()
            } catch (err) {
                console.warn('[QPM] Span error end failed:', err.message)
            }
        }

        // 큐베이스 점검(503 MAINTENANCE / 전환 중 연결 끊김) 관찰 — 로그아웃 사유가 아니다
        observeAxiosError(error)

        const { response } = error

        if (response && unauthorizedCode.includes(response.status)) {
            store.dispatch(onSignOutSuccess())
        }

        return Promise.reject(error)
    }
)

export default BaseService
